<template>
  <!--
    The root is this page's scroller — unless it is rendered inside a community's own page, which
    owns both the scroller and the sticky navigation above it. See isEmbeddedInParentPage.
  -->
  <div
    class="bg-slate-50 text-slate-900"
    :class="isEmbeddedInParentPage ? '' : 'h-full overflow-y-auto'"
    ref="scrollable"
  >

    <!-- ─────────── Loading ─────────── -->
    <!--
      A skeleton of the page that is coming rather than a spinner: the shape is known before the
      data is, so nothing jumps once it lands.
    -->
    <template v-if="detailState === 'loading'">
      <div class="h-[300px] bg-slate-200 animate-pulse"/>
      <div class="h-[49px] bg-white border-b border-gray-100"/>
      <Container class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 lg:gap-x-10 items-start pt-6 pb-12 md:px-8">
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6 grid gap-3 animate-pulse">
          <span class="h-5 w-2/5 rounded bg-slate-200"/>
          <span class="h-3 rounded bg-slate-200" v-for="line in 4" :key="'lead' + line"/>
          <span class="h-3 w-3/5 rounded bg-slate-200"/>
        </div>
        <div class="grid gap-5">
          <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6 grid gap-3 animate-pulse">
            <span class="h-2.5 w-1/3 rounded bg-slate-200"/>
            <span class="h-16 rounded bg-slate-200"/>
          </div>
          <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6 grid gap-3 animate-pulse">
            <span class="h-2.5 w-1/3 rounded bg-slate-200"/>
            <span class="h-3 rounded bg-slate-200"/>
            <span class="h-3 w-2/3 rounded bg-slate-200"/>
          </div>
        </div>
      </Container>
    </template>

    <!-- ─────────── Gone, or not ours to show ─────────── -->
    <div
      class="grid place-content-center justify-items-center gap-4 min-h-[60vh] px-6 py-16 text-center"
      v-else-if="detailState === 'removed'"
    >
      <Icon name="information-outline" size="large" class="text-slate-300"/>
      <h2 class="m-0 max-w-[30ch] text-[22px] font-semibold tracking-tight">
        {{ $t('challenge_detail_removed', { challenge: challengeLabel }) }}
      </h2>
      <button
        class="cd-btn cd-btn--primary"
        type="button"
        @click="goBack"
      >
        {{ $t('challenge_detail_view_all', { challenges: challengesLabel }) }}
      </button>
    </div>

    <div
      class="grid place-content-center justify-items-center gap-4 min-h-[60vh] px-6 py-16 text-center"
      v-else-if="detailState === 'forbidden'"
    >
      <Icon name="lock" size="large" class="text-slate-300"/>
      <h2 class="m-0 max-w-[30ch] text-[22px] font-semibold tracking-tight">
        {{ $t('challenge_detail_no_access', { challenge: challengeLabel }) }}
      </h2>
      <button
        class="cd-btn cd-btn--primary"
        type="button"
        v-if="!isLoggedIn"
        @click="showLoginModal"
      >
        {{ $t('challenge_detail_log_in') }}
      </button>
      <a
        class="cd-btn cd-btn--primary"
        href="/profile/subscription#billing"
        v-else-if="canShowSubscriptions"
      >
        {{ $t('upgrade') }}
      </a>
    </div>

    <!-- ─────────── The challenge ─────────── -->
    <template v-else>
      <!--
        Everything a reader needs in order to decide whether to stay: who is asking, by when, and
        the ways to act on it. The organisation used to sit in a card that hung over the hero from
        the sidebar — it belongs to the title, so it reads with it now.
      -->
      <header
        class="relative bg-slate-900 bg-cover bg-center"
        :style="{ backgroundImage: `url('${heroImage}')` }"
      >
        <!-- A user-uploaded banner can be anything, so the copy gets a ground of its own rather
             than trusting the photograph to be dark where the words are. -->
        <div class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/75 to-slate-900/90"/>

        <Container class="relative tw-flex flex-col items-start gap-4 min-h-[300px] pt-6 pb-8 md:px-8 text-white">
          <button
            class="tw-flex items-center gap-2 mb-auto border-0 bg-transparent p-0 text-xs font-semibold text-white/80 cursor-pointer hover:text-white hover:underline"
            type="button"
            @click="goBack"
          >
            <Icon name="arrow-left" size="12"/>
            {{ $t('back_to', { item: backLabel }) }}
          </button>

          <div class="tw-flex flex-wrap gap-2">
            <span class="cd-tag cd-tag--solid" v-if="statusLabel">{{ statusLabel }}</span>
            <span class="cd-tag">{{ visibilityLabel }}</span>
            <span class="cd-tag" v-if="endDateLabel">
              <Icon name="clock" size="12"/>
              {{ isExpired ? $t('countdown_expired') : $t('challenge_detail_open_until', { date: endDateLabel }) }}
            </span>
          </div>

          <!-- An arbitrary-length title, so it tops out well below a hand-set display size and is
               allowed to break rather than run off the band. -->
          <h1 class="m-0 max-w-[22em] text-[28px] sm:text-[34px] lg:text-[44px] font-bold leading-[1.08] tracking-tight [overflow-wrap:anywhere]">
            {{ challenge.title }}
          </h1>

          <button
            class="tw-flex items-center gap-2.5 border-0 bg-transparent p-0 text-sm font-semibold text-white cursor-pointer hover:underline"
            type="button"
            v-if="organisation"
            @click="goToActorDetail"
          >
            <ActorAvatar
              class="cd-logo"
              :actor="organisation"
              :actor-logo-url="organisation.url"
              :width="36"
            />
            {{ organisation.name }}
            <Icon name="external-link-alt" size="12"/>
          </button>
          <!-- Anonymous: a label, not a way through to a profile that is deliberately not named. -->
          <span class="tw-flex items-center gap-2 text-sm text-white/80" v-else-if="challenge.isAnonymous">
            <Icon name="eye-slash" size="14"/>
            {{ $t('challenge_detail_anonymous') }}
          </span>

          <!--
            One row, one geometry: every button is the same height and the same type size, so the
            only thing that separates them is their fill. Follow carries a floor width because its
            label changes on click, and a button that resizes under the cursor shoves the one
            beside it out from under the pointer.
          -->
          <div class="tw-flex flex-wrap items-center gap-2.5 mt-1">
            <button
              class="cd-btn cd-btn--light"
              type="button"
              v-if="canContact"
              @click="startConversation(contact.id)"
            >
              <Icon name="paper-plane" size="14"/>
              {{ $t('get_in_touch') }}
            </button>
            <button
              class="cd-btn min-w-[8.5rem]"
              :class="challenge.isFollowing ? 'cd-btn--glass-on' : 'cd-btn--glass'"
              type="button"
              v-if="canFollow"
              :disabled="togglingPinStatus"
              :aria-pressed="challenge.isFollowing ? 'true' : 'false'"
              @click="togglePinStatus"
            >
              <Icon :name="challenge.isFollowing ? 'check' : 'pin-outline'" size="14"/>
              <span class="first-letter:uppercase">
                {{ challenge.isFollowing ? $t('intelligence_simplified_following') : $t('intelligence_simplified_follow') }}
              </span>
            </button>
            <button
              class="cd-btn cd-btn--quiet"
              type="button"
              v-if="canUserEditChallenge"
              @click="showEditChallengePanel"
            >
              <Icon name="edit" size="14"/>
              {{ $t('actor_edit') }}
            </button>
          </div>
        </Container>
      </header>

      <!--
        Links rather than buttons, so every tab is a URL somebody can share and the browser's own
        back button walks them. Sticky only when this page owns the scroller — embedded, the
        community page's tab bar is already sticky above this one.
      -->
      <nav
        class="bg-white border-b border-gray-100"
        :class="isEmbeddedInParentPage ? '' : 'sticky top-0 z-[18]'"
        :aria-label="challengeLabel"
      >
        <!-- A narrow screen scrolls the row rather than wrapping it into two rows of tabs. -->
        <Container class="cd-tabs tw-flex gap-1 overflow-x-auto md:px-8">
          <router-link
            v-for="tab of tabs"
            :key="tab.key"
            class="tw-flex items-center gap-2 shrink-0 border-b-2 px-3.5 pt-4 pb-3.5 text-[13px] font-semibold no-underline transition-colors"
            :class="activeTab === tab.key
              ? 'text-primary border-b-primary'
              : 'text-slate-500 border-b-transparent hover:text-slate-900'"
            :to="{ query: $route.query, hash: '#' + tab.key }"
          >
            {{ tab.label }}
            <span
              class="rounded-full px-1.5 text-[11px] font-semibold"
              :class="activeTab === tab.key ? 'bg-primary-extra-lightest text-primary' : 'bg-slate-100 text-slate-500'"
              v-if="tab.count"
            >{{ tab.count }}</span>
          </router-link>
        </Container>
      </nav>

      <Container
        class="grid grid-cols-1 gap-5 lg:gap-x-10 items-start pt-6 md:pt-8 pb-12 md:px-8"
        :class="activeTab === 'match' ? 'lg:grid-cols-1' : 'lg:grid-cols-[minmax(0,1fr)_320px]'"
      >
        <div class="grid gap-5 min-w-0">

          <!-- ── Overview ── -->
          <template v-if="activeTab === 'overview'">
            <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="hasContent(description)">
              <RichBodyContent class="cd-prose cd-prose--lead" :html="description"/>
            </div>

            <section
              class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6"
              v-for="section of overviewSections"
              :key="section.key"
            >
              <h2 class="m-0 mb-3 text-[17px] font-semibold tracking-tight">{{ section.label }}</h2>
              <RichBodyContent class="cd-prose" :html="section.html"/>
            </section>

            <div
              class="grid justify-items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center"
              v-if="!hasContent(description) && !overviewSections.length"
            >
              <Icon name="file-text" size="large" class="text-slate-300"/>
              <strong class="text-[15px]">{{ $t('challenge_detail_no_description') }}</strong>
              <p class="m-0 max-w-[42ch] text-[13px] leading-relaxed text-slate-500">
                {{ $t('challenge_detail_no_description_hint', { challenge: challengeLabel }) }}
              </p>
            </div>
          </template>

          <!-- ── Q&A ── -->
          <section class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-else-if="activeTab === 'qanda'">
            <h2 class="m-0 mb-1.5 text-[17px] font-semibold tracking-tight">{{ $t('challenges_navigation_qanda') }}</h2>
            <p class="m-0 mb-5 text-[13px] leading-relaxed text-slate-500">
              {{ $t('challenge_detail_qanda_intro', { challenge: challengeLabel }) }}
            </p>
            <Comments
              :comments="comments"
              :comments-limit="100"
              :resource-id="String(challenge.id)"
              :show-title="false"
              :only-show-add-comment-input="false"
              @add-comment="addComment"
              @delete-comment="fetchComments"
            />
          </section>

          <!-- ── Updates ── -->
          <template v-else-if="activeTab === 'updates'">
            <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6 grid gap-3 animate-pulse" v-if="isLoadingUpdates">
              <span class="h-2.5 w-1/4 rounded bg-slate-200"/>
              <span class="h-3 rounded bg-slate-200"/>
              <span class="h-3 w-3/5 rounded bg-slate-200"/>
            </div>

            <template v-else>
              <article
                class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6"
                v-for="update of updates"
                :key="update.id"
              >
                <header class="tw-flex flex-wrap items-center justify-between gap-2.5 mb-3">
                  <AvatarNew :user="update.user" :show-details="true"/>
                  <div class="tw-flex items-center gap-1 text-xs text-slate-500">
                    <span v-tooltip.top="update.createdAt">{{ fromNow(update.createdAt) }}</span>
                    <button
                      class="cd-iconbtn tw-flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
                      type="button"
                      v-if="update.user.id === userId"
                      v-tooltip.top="$t('delete')"
                      :aria-label="$t('delete')"
                      @click="confirmUpdateDelete(update)"
                    >
                      <Icon name="trash" size="14"/>
                    </button>
                  </div>
                </header>
                <RichBodyContent class="cd-prose" :html="update.content"/>
              </article>

              <div
                class="grid justify-items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center"
                v-if="!updates.length"
              >
                <Icon name="bullhorn" size="large" class="text-slate-300"/>
                <strong class="text-[15px]">{{ $t('challenge_detail_no_updates') }}</strong>
                <p class="m-0 max-w-[42ch] text-[13px] leading-relaxed text-slate-500">
                  {{ $t('challenge_detail_no_updates_hint', { challenge: challengeLabel }) }}
                </p>
              </div>

              <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="canUserEditChallenge">
                <h2 class="m-0 mb-4 text-[17px] font-semibold tracking-tight">{{ $t('challenges_navigation_updates') }}</h2>
                <Comments
                  :only-show-add-comment-input="true"
                  @add-comment="addUpdate"
                />
              </div>
            </template>
          </template>

          <!-- ── Documents ── -->
          <template v-else-if="activeTab === 'documents'">
            <div class="tw-flex justify-end" v-if="canManageDocuments">
              <button
                class="cd-btn cd-btn--primary"
                type="button"
                @click="openCreateResourcePanel"
              >
                <Icon name="plus" size="14"/>
                {{ $t('challenge_detail_add_document') }}
              </button>
            </div>

            <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6 grid gap-3 animate-pulse" v-if="isLoadingDocuments">
              <span class="h-2.5 w-1/4 rounded bg-slate-200"/>
              <span class="h-3 rounded bg-slate-200"/>
              <span class="h-3 w-3/5 rounded bg-slate-200"/>
            </div>

            <template v-else>
              <!--
                The row is the control. The edit and delete affordances are spans rather than
                buttons because a button inside a button is not markup a browser will build.
              -->
              <button
                class="grid grid-cols-[auto_minmax(0,1fr)] sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-3.5 gap-y-2.5 w-full bg-white rounded-xl border border-gray-100 shadow-sm p-4 md:p-5 text-left cursor-pointer transition-shadow hover:shadow-md"
                type="button"
                v-for="document of documents"
                :key="document.id"
                @click="openResource(document)"
              >
                <span class="tw-flex items-center justify-center w-10 h-10 rounded-[10px] bg-primary-extra-lightest text-primary">
                  <Icon :name="documentIcon(document)" size="18"/>
                </span>
                <span class="grid gap-0.5 min-w-0">
                  <strong class="text-sm font-semibold [overflow-wrap:anywhere]">{{ document.title }}</strong>
                  <span class="line-clamp-2 text-xs leading-relaxed text-slate-500" v-if="document.description">
                    {{ document.description }}
                  </span>
                </span>
                <span
                  class="col-start-2 sm:col-start-auto justify-self-start rounded-md border border-gray-200 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-slate-500"
                  v-if="documentTypeLabel(document)"
                >{{ documentTypeLabel(document) }}</span>
                <span class="col-start-2 sm:col-start-auto tw-flex items-center gap-0.5" v-if="canManageDocuments">
                  <span
                    class="cd-iconbtn tw-flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
                    role="button"
                    tabindex="0"
                    v-tooltip.top="$t('actor_edit')"
                    :aria-label="$t('actor_edit')"
                    @click.stop="showEditResourcePanel(document)"
                    @keydown.enter.stop.prevent="showEditResourcePanel(document)"
                  >
                    <Icon name="edit" size="14"/>
                  </span>
                  <span
                    class="cd-iconbtn tw-flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
                    role="button"
                    tabindex="0"
                    v-tooltip.top="$t('delete')"
                    :aria-label="$t('delete')"
                    @click.stop="confirmResourceDelete(document)"
                    @keydown.enter.stop.prevent="confirmResourceDelete(document)"
                  >
                    <Icon name="trash" size="14"/>
                  </span>
                </span>
              </button>

              <div
                class="grid justify-items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center"
                v-if="!documents.length"
              >
                <Icon name="file" size="large" class="text-slate-300"/>
                <strong class="text-[15px]">{{ $t('challenge_detail_no_documents') }}</strong>
                <p class="m-0 max-w-[42ch] text-[13px] leading-relaxed text-slate-500">
                  {{ $t('challenge_detail_no_documents_hint', { challenge: challengeLabel }) }}
                </p>
              </div>
            </template>
          </template>

          <!-- ── Match ── -->
          <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-else-if="activeTab === 'match'">
            <ChallengeMatchTab :challenge-id="challengeId" :challenge="challenge"/>
          </div>
        </div>

        <!--
          The facts, once, beside every tab — dropped only on Match, where the actor cards need the
          full measure. The hero carries the same deadline and the same contact action, so this is
          where the detail behind them lives rather than a second set of controls.
        -->
        <aside class="grid gap-5 min-w-0" v-if="activeTab !== 'match'">
          <!-- The deadline is the one thing here that expires, so it is the one card that is tinted. -->
          <div
            class="grid justify-items-start gap-3 rounded-xl border border-primary-lightest bg-primary-extra-lightest p-5 md:p-6"
            v-if="endDateLabel"
          >
            <span class="cd-overline">{{ $t('manage_challenge_subtitle_end_date') }}</span>
            <strong class="text-[17px] font-semibold tracking-tight">{{ endDateLabel }}</strong>
            <div class="grid grid-cols-3 gap-2 w-full text-center" v-if="countdown">
              <div class="rounded-[10px] bg-white/70 px-1 py-2.5">
                <b class="block text-[22px] font-bold leading-tight tracking-tight tabular-nums">{{ countdown.days }}</b>
                <span class="cd-overline mt-0.5">{{ $t('countdown_full_days') }}</span>
              </div>
              <div class="rounded-[10px] bg-white/70 px-1 py-2.5">
                <b class="block text-[22px] font-bold leading-tight tracking-tight tabular-nums">{{ countdown.hours }}</b>
                <span class="cd-overline mt-0.5">{{ $t('countdown_full_hours') }}</span>
              </div>
              <div class="rounded-[10px] bg-white/70 px-1 py-2.5">
                <b class="block text-[22px] font-bold leading-tight tracking-tight tabular-nums">{{ countdown.minutes }}</b>
                <span class="cd-overline mt-0.5">{{ $t('countdown_full_minutes') }}</span>
              </div>
            </div>
            <span class="text-[13px] font-semibold text-slate-500" v-else>{{ $t('countdown_expired') }}</span>
          </div>

          <div class="grid justify-items-start gap-3.5 bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="contact">
            <span class="cd-overline">{{ $t('challenge_detail_contact') }}</span>
            <AvatarNew :user="contact" :show-details="true" :show-department="true"/>
            <button
              class="cd-btn cd-btn--primary cd-btn--block"
              type="button"
              v-if="canContact"
              @click="startConversation(contact.id)"
            >
              <Icon name="paper-plane" size="14"/>
              {{ $t('get_in_touch') }}
            </button>
          </div>

          <div class="grid justify-items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="organisation">
            <span class="cd-overline">{{ $t('challenge_detail_organisation') }}</span>
            <button
              class="cd-org tw-flex items-center gap-3 border-0 bg-transparent p-0 text-left cursor-pointer"
              type="button"
              @click="goToActorDetail"
            >
              <ActorAvatar
                class="cd-logo"
                :actor="organisation"
                :actor-logo-url="organisation.url"
                :width="44"
              />
              <strong class="text-sm font-semibold">{{ organisation.name }}</strong>
            </button>
            <p class="m-0 text-[13px] leading-relaxed text-slate-500" v-if="organisationSummary">{{ organisationSummary }}</p>
            <button
              class="tw-flex items-center gap-1.5 border-0 bg-transparent p-0 text-xs font-semibold text-primary cursor-pointer hover:underline"
              type="button"
              @click="goToActorDetail"
            >
              {{ $t('challenge_detail_view_profile') }}
              <Icon name="external-link-alt" size="12"/>
            </button>
          </div>

          <div class="grid justify-items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="goalLabels.length">
            <!-- "Goals" is what the edit panel calls these, so it is what they are called here. -->
            <span class="cd-overline">{{ $t('challenges_goals') }}</span>
            <div class="tw-flex flex-wrap gap-1.5">
              <span class="cd-chip" v-for="area of visibleGoalLabels" :key="area">{{ area }}</span>
            </div>
            <button
              class="tw-flex items-center gap-1.5 border-0 bg-transparent p-0 text-xs font-semibold text-primary cursor-pointer hover:underline"
              type="button"
              v-if="hasHiddenGoalLabels"
              @click="goalsExpanded = !goalsExpanded"
            >
              {{ goalsExpanded ? $t('challenge_detail_show_less') : $t('challenge_detail_show_all', { count: goalLabels.length }) }}
            </button>
          </div>

          <div class="grid justify-items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="geographicalLabels.length">
            <span class="cd-overline">{{ $t('manage_challenge_subtitle_location') }}</span>
            <div class="tw-flex flex-wrap gap-1.5">
              <span class="cd-chip" v-for="(label, index) of geographicalLabels" :key="'geo' + index">
                <Icon name="map-marker" size="12"/>
                {{ label }}
              </span>
            </div>
          </div>

          <!--
            Rendered here rather than with ActorDetailSDGs: that component's scoped styles are
            built for the legacy right-aligned banner (fixed max-widths, clipped at 195px) and
            scoped styles cannot be reshaped from this page. Same images, and read-only — the
            challenge page has never offered the jump into a filtered directory.
          -->
          <div class="grid justify-items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-5 md:p-6" v-if="sustainabilityGoalItems.length">
            <span class="cd-overline">{{ $t('add_challenge_sdg') }}</span>
            <div class="tw-flex flex-wrap gap-1.5">
              <img
                class="block w-12 h-12 rounded-md"
                v-for="goal of sustainabilityGoalItems"
                :key="'sdg' + goal.number"
                v-tooltip.top="goal.label"
                :src="goal.image"
                :alt="goal.label"
                loading="lazy"
              >
            </div>
          </div>
        </aside>
      </Container>

      <!-- ─────────── More to check out ─────────── -->
      <section class="border-t border-gray-100 bg-white pt-11 pb-14" v-if="similarChallenges.length">
        <Container class="md:px-8">
          <h2 class="m-0 mb-5 text-[19px] font-semibold tracking-tight">{{ $t('more_actor_profiles') }}</h2>
          <div class="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5">
            <ChallengeCardNew
              v-for="similarChallenge of similarChallenges"
              :key="similarChallenge.id"
              :challenge="similarChallenge"
            />
          </div>
        </Container>
      </section>
    </template>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import moment from 'moment'
  import _uniqBy from 'lodash/uniqBy'

  import ChallengeMixin from '@/util/ChallengeMixin'
  import TranslationsMixin from '@/util/TranslationsMixin'
  import UserMixin from '@/util/UserMixin'
  import { ConversationsMixin } from '@/util/ConversationsMixin'

  import ActorAvatar from '@/Domain/Actor/Components/ActorAvatar.vue'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import ChallengeCardNew from '@/components/Challenges/ChallengeCardNew.vue'
  import ChallengeMatchTab from '@/components/Challenges/ChallengeMatchTab.vue'
  import Comments from '@/components/Comments.vue'
  import Container from '@/components/Container/Container.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import RichBodyContent from '@/components/RichBodyContent/RichBodyContent.vue'

  import { deleteEcosystemFile } from '@/api/files'
  import { fetchKnowledgeBaseData } from '@/api/knowledge-base'
  import {
    addChallengeUpdate,
    createComment,
    fetchChallengeUpdates,
    fetchSimilarChallenges,
    getComments,
    removeChallengeUpdate,
    updatePinStatus,
  } from '@/Domain/Challenge/Api/challenges'
  import { sustainabilityGoals, sustainabilityGoalsNL } from '@/constants/config'
  import MODAL_IDS from '@/constants/modal-ids'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui'
  import { fromNow } from '@/util/date'
  import { getDefaultFallbackImageUrl, getLabelForChallengeStatus, htmlText } from '@/util/helpers'

  /** The taxonomies that together make up a challenge's focus areas, and the key each translates under. */
  const GOAL_TAXONOMIES = [
    { field: 'activities', taxonomy: 'activities' },
    { field: 'industries', taxonomy: 'industries' },
    { field: 'technology', taxonomy: 'technologies' },
    { field: 'domains', taxonomy: 'domains' },
    { field: 'stages', taxonomy: 'stages' },
  ]

  /** How many goal labels the sidebar shows before it needs a control to show the rest. */
  const GOAL_LABEL_LIMIT = 8

  /** Markup that renders even with no text in it — see hasContent. */
  const EMBEDDED_MEDIA = /<(img|iframe|video|embed)\b/i

  /** A knowledge-base entry whose "file" is somewhere else — its `url` is what to open. */
  const LINK_FILE_TYPES = ['link', 'video_link']

  /** Short format tokens for the badge on a document row. Not copy, so they are not translated. */
  const FILE_TYPE_TOKENS = {
    csv: 'CSV',
    gif: 'GIF',
    jpeg: 'JPG',
    jpg: 'JPG',
    link: 'LINK',
    mp4: 'MP4',
    pdf: 'PDF',
    plain: 'TXT',
    png: 'PNG',
    video_link: 'VIDEO',
    zip: 'ZIP',
  }

  export default defineComponent({
    name: 'challenge-detail-new',
    mixins: [TranslationsMixin, UserMixin, ChallengeMixin, ConversationsMixin],
    components: {
      ActorAvatar,
      AvatarNew,
      ChallengeCardNew,
      ChallengeMatchTab,
      Comments,
      Container,
      Icon,
      RichBodyContent,
    },
    data () {
      return {
        comments: [],
        commentsCount: 0,
        documents: [],
        goalsExpanded: false,
        isLoadingDocuments: false,
        isLoadingUpdates: false,
        // Drives the countdown; ticking one value keeps the arithmetic out of the template.
        now: Date.now(),
        similarLocalChallenges: [],
        tickInterval: null,
        togglingPinStatus: false,
        updates: [],
      }
    },
    computed: {
      challenge () {
        return this.$store.state.challenges.detail.data
      },
      detail () {
        return this.$store.state.challenges.detail
      },
      /**
       * What the page is showing: the challenge, a skeleton of it, or why neither.
       *
       * The store keeps the previously loaded challenge in `detail.data` while the next one is
       * being fetched, so `loading` has to win over having data — otherwise navigating between two
       * challenges shows the old one under the new one's URL.
       */
      detailState (): string {
        if (this.detail.errorId === this.challengeId) {
          return Number(this.detail.errorStatusCode) === 404 ? 'removed' : 'forbidden'
        }

        if (this.isLoadingChallenge || !this.challenge || !this.challenge.id) {
          return 'loading'
        }

        return 'ready'
      },
      /**
       * Rendered inside a community's own page, which owns the scroller and the sticky navigation
       * above this one — so this page stops scrolling and sticking on its own.
       */
      isEmbeddedInParentPage (): boolean {
        return ['community-detail', 'spotting-area-detail'].includes(String(this.$route.name))
      },
      communityId () {
        return this.$route.params.communityId
      },
      canShowSubscriptions (): boolean {
        return this.$store.getters.hasAccessToEcosystemMemberPackages
      },
      isChallengeCreator (): boolean {
        return !!this.challenge.creator && this.challenge.creator.id === this.userId
      },
      canManageDocuments (): boolean {
        return this.isOwner || this.isMember || this.isChallengeCreator
      },
      canDisplayMatchesTab (): boolean {
        return this.isOwner || this.isMember || this.isChallengeCreator
      },
      canFollow (): boolean {
        return this.isLoggedIn && this.canUserAccessChallengeDetail
      },
      canContact (): boolean {
        return !!this.contact && this.canStartConversation(this.contact)
      },
      /** An anonymous challenge names neither an organisation nor a contact. */
      organisation () {
        return this.challenge.isAnonymous ? null : this.challenge.actor
      },
      contact () {
        return this.challenge.isAnonymous ? null : this.challenge.contact
      },
      /**
       * The organisation's own words, as plain text: the description is stored as HTML, so it is
       * rendered to text before it is cut. Slicing the markup leaves a tag open half the time, and
       * stripping the tags with a regex leaves the entities behind as `&amp;`.
       */
      organisationSummary (): string {
        const description = this.organisation && this.localizedDisplayProperty(this.organisation, 'description')

        if (!description) {
          return ''
        }

        const text = htmlText(description).replace(/\s+/g, ' ').trim()

        return text.length > 180 ? text.slice(0, 180).trimEnd() + '…' : text
      },
      heroImage (): string {
        return this.challenge.imageURL || getDefaultFallbackImageUrl()
      },
      isCurationFlow (): boolean {
        return this.$route.query.curation === 'challenge'
      },
      backLabel (): string {
        return this.isCurationFlow ? this.$t('curation') : this.challengesLabel
      },
      statusLabel (): string {
        return getLabelForChallengeStatus.call(this, this.challenge.status)
      },
      visibilityLabel (): string {
        return this.challenge.visibility === 'private' ? this.$t('challenges_private') : this.$t('challenges_public')
      },
      description () {
        return this.localizedDisplayProperty(this.challenge, 'description')
      },
      /** The long-form blocks under the description, in reading order, with the empty ones dropped. */
      overviewSections () {
        return [
          {
            key: 'background',
            label: this.$t('challenge_background_of_the_challenge', { challenge: this.challengeLabel }),
            html: this.localizedDisplayProperty(this.challenge, 'background'),
          },
          {
            key: 'objective',
            label: this.$t('challenges_what_are_we_looking_for'),
            html: this.localizedDisplayProperty(this.challenge, 'objective'),
          },
        ].filter(section => this.hasContent(section.html))
      },
      endDateLabel (): string {
        return this.challenge.endDate ? moment(this.challenge.endDate).format('LL') : ''
      },
      /**
       * The end date is a day, not a moment, and the copy on the edit panel promises the challenge
       * is only marked solved *after* it — so it stays open until that day is over.
       */
      closesAt () {
        return this.challenge.endDate ? moment(this.challenge.endDate).endOf('day') : null
      },
      isExpired (): boolean {
        return !!this.closesAt && this.closesAt.valueOf() <= this.now
      },
      countdown () {
        if (!this.closesAt || this.isExpired) {
          return null
        }

        const remaining = moment.duration(this.closesAt.valueOf() - this.now)

        return {
          days: Math.floor(remaining.asDays()),
          hours: remaining.hours(),
          minutes: remaining.minutes(),
        }
      },
      /**
       * Every taxonomy value the challenge is filed under, in the reader's own language. Deduped:
       * the five taxonomies are separate lists but they do share labels, and the same chip twice
       * reads as a mistake rather than as two classifications that happen to be named alike.
       */
      goalLabels (): string[] {
        const labels = GOAL_TAXONOMIES
          .flatMap(({ field, taxonomy }) => (this.challenge[field] || [])
            .map(item => this.getLabelForTaxonomyValue(taxonomy, item.value || item.id) || item.label))
          .filter(label => !!label)

        return [...new Set(labels)]
      },
      hasHiddenGoalLabels (): boolean {
        return this.goalLabels.length > GOAL_LABEL_LIMIT
      },
      visibleGoalLabels (): string[] {
        if (this.goalsExpanded || !this.hasHiddenGoalLabels) {
          return this.goalLabels
        }

        return this.goalLabels.slice(0, GOAL_LABEL_LIMIT)
      },
      geographicalLabels (): string[] {
        return (this.challenge.geographicalLabels || []).map(geography => geography.label)
      },
      sustainabilityGoalItems () {
        const isDutch = this.$store.getters.activeLocale === 'nl'
        const labels = isDutch ? sustainabilityGoalsNL : sustainabilityGoals
        const path = isDutch ? '/images/SDG_nl/' : '/images/SDG/'

        return (this.challenge.sdgs || [])
          .map(sdg => {
            const label = String(sdg.label || '')

            return Number(label.substring(label.indexOf(' ') + 1, label.indexOf(':')))
          })
          // A goal saved in a format we cannot parse would otherwise ask for /images/SDG/NaN.jpg.
          .filter(number => number >= 1 && number <= sustainabilityGoals.length)
          .sort((a, b) => a - b)
          .map(number => ({
            number,
            label: labels[number - 1],
            image: `${path}${number}.jpg`,
          }))
      },
      tabs () {
        return [
          { key: 'overview', label: this.$t('challenges_navigation_overview'), count: 0 },
          { key: 'qanda', label: this.$t('challenges_navigation_qanda'), count: this.commentsCount },
          { key: 'updates', label: this.$t('challenges_navigation_updates'), count: this.updates.length },
          { key: 'documents', label: this.$t('challenges_navigation_documents'), count: this.documents.length },
          { key: 'match', label: this.$t('challenges_navigation_match'), count: 0, hidden: !this.canDisplayMatchesTab },
        ].filter(tab => !tab.hidden)
      },
      /**
       * The hash is the only place the open tab is kept, so a tab is a URL somebody can share and
       * the browser's back button walks them. A hash naming a tab this reader cannot open — #match
       * on someone else's challenge — falls back to the overview rather than an empty panel.
       */
      activeTab (): string {
        const hash = String(this.$route.hash || '').replace('#', '')

        return this.tabs.some(tab => tab.key === hash) ? hash : 'overview'
      },
      /** Similar challenges first, then whatever the list page has already loaded, minus this one. */
      similarChallenges () {
        const combined = this.similarLocalChallenges
          .concat(this.challenges)
          .filter(challenge => challenge.id !== this.challengeId)

        return _uniqBy(combined, 'id').slice(0, 3)
      },
    },
    methods: {
      fromNow,
      /**
       * True for HTML that renders as something. An empty `<p></p>` does not, and neither does a
       * body of nothing but entities — but a body of nothing but an image does.
       */
      hasContent (html): boolean {
        if (!html) {
          return false
        }

        const value = String(html)

        return htmlText(value).trim().length > 0 || EMBEDDED_MEDIA.test(value)
      },
      goBack () {
        if (this.communityId) {
          if (this.isCurationFlow) {
            return this.$router.push(`/communities/${this.communityId}/curation?curation=challenge`)
          }

          return this.$router.push(`/communities/${this.communityId}/challenges`)
        }

        return this.$router.push('/challenges')
      },
      goToActorDetail () {
        if (!this.organisation) {
          return
        }

        window.open(`/actors/${this.organisation.elastic_id}`, '_blank').focus()
      },
      showLoginModal () {
        window.redirect_uri = window.location.href
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.LOGIN)
      },
      togglePinStatus () {
        this.togglingPinStatus = true

        updatePinStatus(this.challenge.id)
          .then(response => {
            this.challenge.isFollowing = response.isFollowing
          })
          .finally(() => {
            this.togglingPinStatus = false
          })
      },

      /* --- data -------------------------------------------------------- */

      /**
       * Everything the page needs, in parallel: none of these depend on the detail response — they
       * are all keyed on the id in the route.
       */
      loadChallenge () {
        if (!this.challengeId || Number.isNaN(this.challengeId)) {
          return
        }

        this.fetchChallengeDetail(this.challengeId)
        this.fetchComments()
        this.fetchDocuments()
        this.fetchUpdates()
        this.loadSimilarChallenges()
      },
      loadSimilarChallenges () {
        fetchSimilarChallenges(this.challengeId)
          .then(result => {
            this.similarLocalChallenges = result
          })
      },
      fetchComments () {
        return getComments(this.challengeId)
          .then(data => {
            this.comments = data.comments
            this.commentsCount = data.count
          })
          .catch(() => {
            this.comments = []
            this.commentsCount = 0
          })
      },
      addComment (comment) {
        createComment(this.challenge.id, comment)
          .then(() => this.fetchComments())
      },
      fetchUpdates () {
        this.isLoadingUpdates = true

        fetchChallengeUpdates(this.challengeId)
          .then(response => {
            this.updates = response
          })
          .finally(() => {
            this.isLoadingUpdates = false
          })
      },
      addUpdate (content) {
        addChallengeUpdate(this.challengeId, { content: content, challenge_id: this.challengeId })
          .then(() => this.fetchUpdates())
      },
      fetchDocuments () {
        this.isLoadingDocuments = true

        return fetchKnowledgeBaseData({ linked_challenge_id: this.challengeId })
          .then(response => {
            this.documents = (response && response.data && response.data.results) || []
          })
          .finally(() => {
            this.isLoadingDocuments = false
          })
      },

      /* --- documents --------------------------------------------------- */

      documentMetaValue (document, key) {
        const entry = (document.meta || []).find(meta => meta.key === key)

        return entry ? entry.value : null
      },
      documentFileType (document): string {
        return String(this.documentMetaValue(document, 'file_type') || '').toLowerCase()
      },
      isLinkDocument (document): boolean {
        return LINK_FILE_TYPES.includes(this.documentFileType(document))
      },
      documentIcon (document): string {
        const type = this.documentFileType(document)

        if (this.isLinkDocument(document)) return 'file-link'
        if (type.includes('pdf')) return 'file-pdf'
        if (type.includes('sheet') || type.includes('excel') || type.includes('csv')) return 'file-spreadsheet'
        if (type.includes('presentation') || type.includes('powerpoint')) return 'file-powerpoint'
        if (type.includes('word')) return 'file-word'
        if (type.startsWith('image')) return 'file-image'
        if (type.startsWith('video')) return 'file-video'
        if (type.startsWith('audio')) return 'file-audio'
        if (type.includes('zip')) return 'file-zip'

        return 'file'
      },
      documentTypeLabel (document): string {
        const type = this.documentFileType(document)

        if (!type) {
          return ''
        }

        // A real mimetype is "type/subtype"; the link pseudo-types are a bare word.
        const subtype = type.split('/')[1] || type

        if (FILE_TYPE_TOKENS[subtype]) return FILE_TYPE_TOKENS[subtype]
        if (subtype.includes('sheet') || subtype.includes('excel')) return 'XLS'
        if (subtype.includes('presentation') || subtype.includes('powerpoint')) return 'PPT'
        if (subtype.includes('word')) return 'DOC'

        return subtype.slice(0, 4).toUpperCase()
      },
      openResource (document) {
        const url = this.documentMetaValue(document, 'url')

        if (this.isLinkDocument(document) && url) {
          window.open(url, '_blank')
          return
        }

        if (document.sql_media_id) {
          window.open(`${window.location.origin}/api/files/${document.sql_media_id}`, '_blank')
        }
      },
      openCreateResourcePanel () {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'create-resource-panel',
          metaData: 'resource',
        })
      },
      showEditResourcePanel (document) {
        this.$store.commit(UI_MUTATION_TYPES.SHOW_SIDE_PANEL, {
          component: 'edit-resource-panel',
          metaData: { resource: document },
        })
      },

      /* --- deletions, all confirmed through the shared modal ----------- */

      confirmResourceDelete (resource) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          body: 'Do you want to delete this resource?',
          resource: resource,
          modalContextType: 'file',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      confirmUpdateDelete (update) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          name: 'challenge-update',
          resource: update,
          modalContextType: 'challenge-update',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      onResourceDeleteConfirmed (modalContext) {
        const mediaId = modalContext.resource && modalContext.resource.sql_media_id

        if (!mediaId) {
          return
        }

        deleteEcosystemFile(mediaId).then(() => this.fetchDocuments())
      },
      onUpdateDeleteConfirmed (modalContext) {
        removeChallengeUpdate(modalContext.resource.challengeId, modalContext.resource.id)
          .then(() => this.fetchUpdates())
      },
      /** The create and edit resource panels report back here once they have saved. */
      onDocumentsChanged (challengeId) {
        if (Number(challengeId) !== this.challengeId) {
          return
        }

        this.fetchDocuments()
      },

      /* --- scrolling --------------------------------------------------- */

      /**
       * The element this page actually scrolls in: the root itself on /challenges/:id, the parent
       * page's pane when embedded — see isEmbeddedInParentPage.
       */
      resolveScrollContainer () {
        const root = this.$refs.scrollable

        if (!root) {
          return null
        }

        if (!this.isEmbeddedInParentPage) {
          return root
        }

        for (let node = root.parentElement; node; node = node.parentElement) {
          const { overflowY } = window.getComputedStyle(node)

          if (overflowY === 'auto' || overflowY === 'scroll') {
            return node
          }
        }

        return null
      },
    },
    mounted () {
      this.loadChallenge()

      this.tickInterval = setInterval(() => {
        this.now = Date.now()
      }, 30000)

      this.$bus.on('challengeUpdateDeleteConfirmation', this.onUpdateDeleteConfirmed)
      this.$bus.on('fetch-challenge-documents', this.onDocumentsChanged)
      this.$bus.on('fileDeleteConfirmation', this.onResourceDeleteConfirmed)
    },
    beforeUnmount () {
      clearInterval(this.tickInterval)

      this.$bus.off('challengeUpdateDeleteConfirmation', this.onUpdateDeleteConfirmed)
      this.$bus.off('fetch-challenge-documents', this.onDocumentsChanged)
      this.$bus.off('fileDeleteConfirmation', this.onResourceDeleteConfirmed)
    },
    watch: {
      challengeId (id) {
        if (!id || Number.isNaN(id)) {
          return
        }

        this.goalsExpanded = false
        this.loadChallenge()

        const container = this.resolveScrollContainer()

        if (container) {
          container.scrollTop = 0
        }
      },
    },
  })
</script>

<!--
  Tailwind carries the layout and the type; this block is only what it cannot reach: the primitives
  that repeat often enough to name, and the styling of markup that arrives from another component.
-->
<style lang="scss" scoped>
  /* --- primitives ------------------------------------------------------ */

  .cd-overline {
    @apply block text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400;
  }

  /* `inline-flex` rather than the app's `tw-flex`: @apply only reaches Tailwind's own utilities,
     and `tw-flex` is a project class declared in tailwind.scss. */
  .cd-chip {
    @apply inline-flex items-center gap-1.5 rounded-md bg-primary-extra-lightest px-2 py-1 text-xs leading-snug text-primary;
  }

  /* The hero's metadata pills. Both fills live here rather than beside the class in the template,
     because a utility and a scoped class carry the same weight and the winner would come down to
     which stylesheet the bundler happened to write last. */
  .cd-tag {
    @apply inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white;

    border-color: rgba(255, 255, 255, .4);

    /* The status is the one fact worth a fill; the rest of the row is context. */
    &--solid {
      border-color: transparent;
      background: var(--primary-community);
    }
  }

  .cd-iconbtn {
    @apply border-0 bg-transparent text-slate-400 cursor-pointer;

    &:hover {
      @apply bg-slate-100 text-slate-700;
    }
  }

  .cd-org:hover strong {
    @apply text-primary;
  }

  /**
   * A square tile for the organisation's logo.
   *
   * ActorAvatar sizes its own box but leaves the image at `min-width: 100%` with no height cap and
   * no object-fit, so a wide logo runs out of the box and a tall one runs out of the bottom.
   * `contain` inside a fixed square keeps every logo whole and square, and the white ground is
   * what most logos are drawn for — on the hero's dark band a transparent PNG would otherwise
   * disappear into it.
   */
  .cd-logo {
    @apply shrink-0 overflow-hidden rounded-lg bg-white;

    :deep(img) {
      width: 100%;
      height: 100%;
      padding: 3px;
      box-sizing: border-box;
      object-fit: contain;
    }
  }

  /**
   * Icons take the colour of whatever they sit in.
   *
   * A handful of icons in the manifest hardcode a fill on their paths — `edit` and `check` are
   * #515151, `clock` #9B9B9B, `map-marker` #5E5E5E — and `plus` ships `fill="none"` expecting a
   * `.fill-fg` rule to fill it in. A path's own fill attribute beats the `fill: currentColor` the
   * Icon component sets on the container, so on the dark hero the edit pencil came out grey and
   * the plus came out invisible. Same override the app's own iconColor() mixin does for `.button`.
   *
   * None of the icons on this page are stroke-drawn, so forcing the fill cannot flatten an
   * outline into a solid shape. Confined to this page's own primitives rather than the root, so
   * the child components rendered here keep the icon colours they have everywhere else.
   */
  .cd-btn,
  .cd-tag,
  .cd-chip,
  .cd-iconbtn {
    :deep(.svg-icon path),
    :deep(.svg-icon circle),
    :deep(.svg-icon rect) {
      fill: currentColor !important;
    }
  }

  /**
   * One button, four fills. The geometry lives here rather than beside each variant in the
   * template so a row of them cannot drift apart: same height, same padding, same type size,
   * `leading-none` so the icon and the label share one centre line.
   */
  .cd-btn {
    @apply inline-flex items-center justify-center gap-2 h-10 rounded-lg border border-transparent px-4 text-[13px] font-semibold leading-none no-underline cursor-pointer transition-colors;

    &:disabled {
      @apply opacity-50 cursor-default;
    }

    &:focus-visible {
      outline: 2px solid var(--primary-community);
      outline-offset: 2px;
    }

    /* The ecosystem's own colour, and its darker shade for hover — which the Tailwind theme
       exposes as a variable but not as a token. */
    &--primary {
      background: var(--primary-community);
      color: #fff;

      &:hover {
        background: var(--primary-community-dark, var(--primary-community));
        color: #fff;
      }
    }

    /* On the hero image, where a tinted fill would compete with the photograph behind it. */
    &--light {
      background: #fff;
      color: #0f172a;

      &:hover {
        background: var(--primary-community-extra-lightest);
        color: var(--primary-community);
      }
    }

    &--glass {
      background: rgba(255, 255, 255, .14);
      border-color: rgba(255, 255, 255, .5);
      color: #fff;

      &:hover {
        background: rgba(255, 255, 255, .26);
        border-color: #fff;
      }
    }

    /* Following, on the image: a filled toggle rather than a second white button, which would
       read as a call to action next to the one that already is. */
    &--glass-on {
      background: rgba(255, 255, 255, .32);
      border-color: #fff;
      color: #fff;

      &:hover {
        background: rgba(255, 255, 255, .2);
      }
    }

    /* Owner tools sit a tier below the actions a visitor came here for. */
    &--quiet {
      background: transparent;
      border-color: rgba(255, 255, 255, .28);
      color: rgba(255, 255, 255, .85);

      &:hover {
        border-color: rgba(255, 255, 255, .7);
        color: #fff;
      }
    }

    &--block {
      @apply w-full;
    }

    /* The accent ring is what the rest of the page focuses with, but it disappears against the
       hero photograph — so the buttons that live on the image focus in white instead. */
    &--light:focus-visible,
    &--glass:focus-visible,
    &--glass-on:focus-visible,
    &--quiet:focus-visible {
      outline-color: #fff;
    }
  }

  /* --- borrowed markup ------------------------------------------------- */

  /**
   * The challenge's own prose, rendered by RichBodyContent — so these have to out-specify that
   * component's global `.rich-body-content` (0.875rem type and a 3rem bottom margin, both of which
   * are meant for the announcement feed) and reach past it into the sanitised HTML.
   */
  .cd-prose {
    margin-bottom: 0;
    font-size: 15px;
    line-height: 1.7;
    color: #414b56;

    /* The lead is the challenge itself — one step up from the sections that explain it. */
    &--lead {
      font-size: 17px;
      color: #1f2933;
    }

    :deep(p:last-child),
    :deep(ul:last-child),
    :deep(ol:last-child) {
      margin-bottom: 0;
    }

    :deep(a) {
      color: var(--primary-community);
    }

    :deep(img) {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
    }
  }

  /* The tab row scrolls sideways on a narrow screen; the bar it sits in is 49px tall, so a
     scrollbar inside it would take a quarter of that. */
  .cd-tabs {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
</style>
